/* eslint-disable react/display-name */
import React, {forwardRef, useEffect, useState} from 'react';
import {NavLink as RouterLink} from 'react-router-dom';
import clsx from 'clsx';
import {makeStyles} from '@material-ui/styles';
import {Button, Collapse, colors, ListItem, Theme} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

const CustomRouterLink = forwardRef((props: any, ref: any) => (
    <div
        ref={ref}
        style={{flexGrow: 1}}
    >
        <RouterLink {...props} />
    </div>
));

const useStyles = makeStyles((theme: Theme) => ({
    item: {
        display: 'block',
        paddingTop: 0,
        paddingBottom: 0
    },
    itemLeaf: {
        display: 'flex',
        paddingTop: 0,
        paddingBottom: 0
    },
    button: {
        color: colors.blueGrey[800],
        padding: '10px 8px',
        justifyContent: 'flex-start',
        textTransform: 'none',
        letterSpacing: 0,
        width: '100%'
    },
    buttonLeaf: {
        color: colors.blueGrey[800],
        padding: '10px 8px',
        justifyContent: 'flex-start',
        textTransform: 'none',
        letterSpacing: 0,
        width: '100%',
        fontWeight: theme.typography.fontWeightRegular,
        '&.depth-0': {
            fontWeight: theme.typography.fontWeightMedium
        }
    },
    icon: {
        color: theme.palette.icon,
        display: 'flex',
        alignItems: 'center',
        marginRight: theme.spacing(1)
    },
    expandIcon: {
        marginLeft: 'auto',
        height: 16,
        width: 16
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto'
    },
    active: {
        color: theme.palette.primary.main,
        fontWeight: theme.typography.fontWeightMedium,
        '& $icon': {
            color: theme.palette.primary.main
        }
    }
}));

const NavigationListItem = (props: {
    children?: any,
    className?: string,
    depth: number,
    href?: string,
    icon?: any,
    label?: any,
    open?: boolean,
    title: string,
    onNavigationItemClick?: () => void,
}) => {
    const {
        title,
        href,
        depth = 0,
        children,
        icon: Icon,
        className,
        label: Label,
        onNavigationItemClick,
        ...rest
    } = props;

    const classes = useStyles();
    const [open, setOpen] = useState(() => {
        const storedValue = localStorage.getItem(`openState_${title}`);
        return storedValue ? JSON.parse(storedValue) : false;
    });

    const handleToggle = () => {
        setOpen((isOpen: any) => !isOpen);
    };

    useEffect(() => {
        // Save the current state to local storage whenever it changes
        localStorage.setItem(`openState_${title}`, JSON.stringify(open));
    }, [open, title]);

    let paddingLeft = 8;

    if (depth > 0) {
        paddingLeft = 32 + 8 * depth;
    }

    const style = {
        paddingLeft
    };

    if (children) {
        return (
            <ListItem
                {...rest}
                className={clsx(classes.item, className)}
                disableGutters
            >
                <Button
                    className={classes.button}
                    onClick={handleToggle}
                    style={style}
                >
                    {Icon && <Icon className={classes.icon}/>}
                    {title}
                    {open ? (
                        <ExpandLessIcon
                            className={classes.expandIcon}
                            color="inherit"
                        />
                    ) : (
                        <ExpandMoreIcon
                            className={classes.expandIcon}
                            color="inherit"
                        />
                    )}
                </Button>
                <Collapse in={open}>{children}</Collapse>
            </ListItem>
        );
    } else {
        return (
            <ListItem
                {...rest}
                className={clsx(classes.itemLeaf, className)}
                disableGutters
                onClick={onNavigationItemClick}
            >
                <Button
                    activeClassName={classes.active}
                    className={clsx(classes.buttonLeaf, `depth-${depth}`)}
                    component={CustomRouterLink}
                    exact
                    style={style}
                    to={href}
                >
                    {Icon && <Icon className={classes.icon}/>}
                    {title}
                    {Label && (
                        <span className={classes.label}>
              <Label/>
            </span>
                    )}
                </Button>
            </ListItem>
        );
    }
};


export default NavigationListItem;
